import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["panel", "input", "messages", "backdrop", "submitButton"]
  static values = {
    open: { type: Boolean, default: false },
    errorMessage: { type: String, default: "Er ging iets mis. Probeer het opnieuw." }
  }

  connect() {
    this._handleEscape = this._handleEscape.bind(this)
    this._handleViewportResize = this._handleViewportResize.bind(this)
    this._observeMessages()
    this.sending = false

    // Mobile keyboard handling: resize panel when virtual keyboard opens
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this._handleViewportResize)
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleEscape)
    this._observer?.disconnect()
    this._clearTypingTimeout()
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._handleViewportResize)
    }
  }

  toggle() {
    this.openValue ? this.close() : this.open()
  }

  open() {
    this.openValue = true
    this.panelTarget.classList.remove("translate-x-full")
    this.panelTarget.classList.add("translate-x-0")
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden")
    }
    document.addEventListener("keydown", this._handleEscape)
    // Focus input after transition
    setTimeout(() => {
      this.inputTarget?.focus()
      this._scrollToBottom()
    }, 300)
  }

  close() {
    this.openValue = false
    this.panelTarget.classList.add("translate-x-full")
    this.panelTarget.classList.remove("translate-x-0")
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
    }
    document.removeEventListener("keydown", this._handleEscape)
  }

  selectExample(event) {
    const question = event.currentTarget.dataset.question
    if (question && this.hasInputTarget) {
      this.inputTarget.value = question
      this.inputTarget.focus()
    }
  }

  async sendMessage(event) {
    event.preventDefault()
    if (this.sending) return

    const message = this.inputTarget.value.trim()
    if (!message) return

    this.sending = true
    this.inputTarget.value = ""
    this._setSubmitDisabled(true)
    this._removeEmptyState()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch("/staff/chat/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin",
        body: new URLSearchParams({ message })
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Start typing timeout — if no response within 90s, show error
        this._startTypingTimeout()
      } else {
        console.error("Chat send failed:", response.status)
      }
    } catch (error) {
      console.error("Chat send error:", error)
    } finally {
      this.sending = false
      this._setSubmitDisabled(false)
      this.inputTarget.focus()
    }
  }

  // Send a pre-defined chip message (from example questions)
  sendChip(event) {
    const message = event.currentTarget.dataset.message
    if (!message) return

    // Make sure panel is open first
    if (!this.openValue) this.open()

    // Set input value and submit
    this.inputTarget.value = message
    this.inputTarget.form.requestSubmit()
  }

  async newSession(event) {
    event.preventDefault()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch("/staff/chat/new_session", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error("New session error:", error)
    }
  }

  _startTypingTimeout() {
    this._clearTypingTimeout()
    this._typingTimer = setTimeout(() => {
      const indicator = document.getElementById("chat_typing_indicator")
      if (indicator) {
        indicator.innerHTML = `
          <div class="flex justify-start gap-2">
            <div class="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <i class="fas fa-exclamation-triangle text-red-500 text-xs"></i>
            </div>
            <div class="bg-white px-4 py-2.5 rounded-2xl text-sm text-red-600 shadow-sm border border-red-100">
              ${this.errorMessageValue}
            </div>
          </div>`
      }
      this.sending = false
      this._setSubmitDisabled(false)
    }, 90_000)
  }

  _clearTypingTimeout() {
    if (this._typingTimer) {
      clearTimeout(this._typingTimer)
      this._typingTimer = null
    }
  }

  // Auto-scroll when new messages arrive
  _observeMessages() {
    if (!this.hasMessagesTarget) return

    this._observer = new MutationObserver(() => {
      this._scrollToBottom()
      // Clear typing timeout when new content arrives (response received)
      this._clearTypingTimeout()
    })

    this._observer.observe(this.messagesTarget, {
      childList: true,
      subtree: true
    })
  }

  _scrollToBottom() {
    if (this.hasMessagesTarget) {
      requestAnimationFrame(() => {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      })
    }
  }

  _setSubmitDisabled(disabled) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = disabled
    }
  }

  _removeEmptyState() {
    const emptyState = this.messagesTarget?.querySelector(".chat-empty-state")
    if (emptyState) emptyState.remove()
  }

  _handleEscape(event) {
    if (event.key === "Escape") this.close()
  }

  // Adjust panel height when mobile virtual keyboard opens/closes
  _handleViewportResize() {
    if (!this.hasPanelTarget || !this.openValue) return

    const viewport = window.visualViewport
    if (!viewport) return

    // Set panel height to visual viewport height (excludes keyboard)
    const height = viewport.height
    this.panelTarget.style.height = `${height}px`
    this.panelTarget.style.top = `${viewport.offsetTop}px`

    // Scroll messages to bottom when keyboard opens
    this._scrollToBottom()
  }
}
